import React, {
  CSSProperties,
  useCallback,
  useRef,
  useLayoutEffect,
  useEffect,
  FC,
  PropsWithChildren,
  useState
} from 'react'
import {
  CellMeasurer,
  CellMeasurerCache,
  InfiniteLoader,
  List
} from 'react-virtualized'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import {
  usePopover,
  Popover,
  Modal,
  ModalBody,
  ModalFooter
} from '@contrast-security-inc/design-system-popovers'
import { Text } from '@contrast-security-inc/design-system-typography'
import { CurrentOrganization } from '../current-organization'
import { CurrentUser } from '../current-user'
import { useNotificationsData, useShowFirstTimeNotification } from './api'
import { useGetTotalVulnerabilitiesPendingApproval } from './api/get-total-vulnerabilities-pending-approval'
import { NotificationRow } from './notification-row'
import { NotificationHeader } from './notification-header'
import { NotificationFooter } from './notification-footer'
import { NotificationSkeleton } from './notification-skeleton'
import { useDownloadFile } from '../../hooks/use-download-file'
import { NotificationsTrigger } from './notifications-trigger'
import { updateRoute, getAccountNotificationsRoute } from '../route'
import { VulnsPendingStatusNotification } from './vulns-pending-status-notification'
import { NotificationRowLayoutGetRef } from './notification-row-layout'
import { FreemiumNewUserNotification } from './freemium-new-user-notification'
import { IconInformation } from '@contrast-security-inc/design-system-foundations'
import { Button } from '@contrast-security-inc/design-system-buttons'
import { formatStyles } from '../react-intl-helpers'
import {
  getRbacStatus,
  userHasAction,
  userHasResourceAction
} from 'contrastBase/accessControl'

const dimensions = {
  defaultHeight: 95,
  height: 400,
  defaultWidth: 400,
  width: 448,
  minWidth: 400
}

export interface NotificationMenuProps {
  currentOrganization: CurrentOrganization
  currentUser: CurrentUser
  isAuthenticated: boolean
}

type FixedTopNotificationType =
  | 'TOP_NOTIFICATION_VULNS_PENDING'
  | 'TOP_NOTIFICATION_FREEMIUM_USER'

export const determineRowCount = ({
  remoteCount,
  loadedCount
}: {
  remoteCount: number
  loadedCount: number
}) => {
  const maxEmptyRows = 10
  let rowCount = remoteCount
  if (loadedCount < remoteCount) {
    const diff = remoteCount - loadedCount
    rowCount =
      diff > maxEmptyRows ? loadedCount + maxEmptyRows : loadedCount + diff
  }
  return rowCount
}

const NotificationLayout: FC<PropsWithChildren> = ({ children }) => (
  <div
    className={css`
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 100%;
    `}
  >
    {children}
  </div>
)
export function Notifications({
  currentOrganization: { organizationUuid, isFreemium },
  currentUser: {
    newUserNotificationPreference,
    setNewUserOnboardingPreferenceCallback,
    uid
  }
}: NotificationMenuProps) {
  const [notificationDialogContent, setNotificationDialogContent] = useState(
    <></>
  )
  const [notificationDialogTitle, setNotificationDialogTitle] = useState('')
  const [isNotificationDialogOpen, setIsNotificationDialogOpen] =
    useState<boolean>(false)

  const intl = useIntl()
  const toggleMenu = () => {
    if (popoverProps.isOpen) {
      popoverProps.onClose(false)
    } else {
      // @ts-ignore fix popoverTriggerProps type
      popoverTriggerProps?.onClick()
    }
  }

  const { popoverTriggerProps, popoverProps } = usePopover({
    hasArrow: true,
    width: dimensions.width
  })

  const {
    notifications,
    remoteCount,
    isLoading,
    requestNextPage,
    markNotificationRead,
    markAllNotificationsRead,
    isMarkAsReadLoading,
    isMarkAllAsReadLoading,
    markAllAsReadTimestamp
  } = useNotificationsData({
    organizationUuid,
    enabled: !!popoverProps.isOpen
  })

  // refs needed to keep scroll position
  const previousRemoteCount = useRef(remoteCount)
  const currentScrollStartIndex = useRef(0)
  const previousScrollStartIndex = useRef(0)
  const listRef = useRef<List | null>(null)
  const isListScrolling = useRef(false)

  const showFirstTimeNotification = useShowFirstTimeNotification({
    isFreemium,
    showFirstTimeNotification: newUserNotificationPreference,
    setNewUserOnboardingPreferenceCallback,
    enabled: !!popoverProps.isOpen
  })

  const { downloadFile } = useDownloadFile()
  const downloadAttestationReport = useCallback(
    (reportId) => {
      const url = `/${organizationUuid}/reports/${reportId}/download`
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      downloadFile(url, { method: 'POST' })
    },
    [downloadFile, organizationUuid, uid]
  )

  const getDefaultReasonContent = useCallback(() => {
    return (
      <p>
        {intl.formatMessage({
          id: 'detail_notificationsDialog_defaultReason'
        })}
      </p>
    )
  }, [intl])

  const buildDialogContent = useCallback(
    (key: string, items: any[]) => {
      if (!key && items.length >= 1 && items[0] !== null) {
        return items.map((item) => {
          return <p key={`${item}-label`}>{intl.formatMessage({ id: item })}</p>
        })
      } else if (key) {
        return (
          <>
            <p key={`${key}-label`}>{key}</p>
            {items.length >= 1 && items[0] !== null && (
              <ul key={key}>
                {items.sort().map((item) => {
                  if (item === null) {
                    return null
                  }
                  return <li key={item}>{intl.formatMessage({ id: item })}</li>
                })}
              </ul>
            )}
          </>
        )
      }

      return null
    },
    [intl]
  )

  const appOnboardingFailed = useCallback(
    (data) => {
      let content, applicationName
      try {
        const dataJson = JSON.parse(decodeURIComponent(data))
        applicationName = dataJson.applicationName
          ? dataJson.applicationName
          : ''
        content = Object.keys(dataJson).map((key) => {
          return key === 'applicationName'
            ? null
            : buildDialogContent(key, dataJson[key])
        })
      } catch (e) {
        content = getDefaultReasonContent()
      }

      if (
        !content ||
        (Array.isArray(content) && content.length === 1 && content[0] === null)
      ) {
        content = getDefaultReasonContent()
      }

      setNotificationDialogContent(
        <>
          <p>
            {applicationName &&
              intl.formatMessage(
                { id: 'detail_appOnboardingFailed_dialogContentApplication' },
                {
                  applicationName,
                  ...formatStyles.bold
                }
              )}
            &nbsp;
            {intl.formatMessage({
              id: 'detail_appOnboardingFailed_dialogContentPrefix'
            })}
          </p>
          {content}
          <br />
          <p>
            {intl.formatMessage({
              id: 'detail_appOnboardingFailed_dialogContentSuffix'
            })}
          </p>
        </>
      )
      setNotificationDialogTitle(
        intl.formatMessage({ id: 'title_appOnboardingFailed_dialog' })
      )
      setIsNotificationDialogOpen(true)
    },
    [
      setNotificationDialogContent,
      intl,
      buildDialogContent,
      getDefaultReasonContent
    ]
  )

  const { isAssessEnforced } = getRbacStatus()
  const userHasRulesAdminRole = isAssessEnforced
    ? userHasResourceAction('APPLICATION', 'APPLICATION_RULES_ADMIN')
    : userHasAction('RULES_ADMIN')

  const cellMeasurerCache = new CellMeasurerCache({
    defaultWidth: dimensions.defaultWidth,
    minWidth: dimensions.minWidth,
    fixedWidth: true,
    defaultHeight: dimensions.defaultHeight,
    keyMapper: (index) => notifications[index]?.id // cache notifications by id instead of index
  })

  const rowCount = determineRowCount({
    remoteCount,
    loadedCount: notifications.length
  })

  const vulnsPendingApprovalResponse =
    useGetTotalVulnerabilitiesPendingApproval({
      organizationUuid,
      enabled: !isFreemium && userHasRulesAdminRole && !!popoverProps.isOpen
    })

  const showVulnsPendingStatusNotification =
    !isFreemium && vulnsPendingApprovalResponse.totalVulnerabilities > 0

  const fixedTopNotificationRows: FixedTopNotificationType[] = []
  if (showFirstTimeNotification) {
    fixedTopNotificationRows.push('TOP_NOTIFICATION_FREEMIUM_USER')
  }
  if (showVulnsPendingStatusNotification) {
    fixedTopNotificationRows.push('TOP_NOTIFICATION_VULNS_PENDING')
  }
  const fixedTopRowCount = fixedTopNotificationRows.length

  const isRowLoaded = useCallback(
    ({ index }) => {
      return !!notifications[index]
    },
    [notifications]
  )

  const routeToAccountNotifications = () => {
    updateRoute(getAccountNotificationsRoute(organizationUuid))
  }

  const getClickHandler = useCallback(
    (type) => {
      return type === 'APP_ONBOARDING_FAILED'
        ? appOnboardingFailed
        : downloadAttestationReport
    },
    [appOnboardingFailed, downloadAttestationReport]
  )

  const preprocessNotification = useCallback((notification) => {
    if (notification.type !== 'APP_ONBOARDING_FAILED') {
      return notification
    }

    try {
      const splitKey1 = '{{#clickMethod}}APP_ONBOARDING_FAILED:'
      const splitKey2 = '$$LINK_DELIM$$'
      const templatePartsFirstPass =
        notification.mustacheTemplate.split(splitKey1)
      const templatePartsSecondPass = templatePartsFirstPass[1].split(splitKey2)
      const failureData = templatePartsSecondPass[0]

      let failureDataJson = {}
      const incompleteMustacheTemplateFill = '{22}'
      if (!!failureData && failureData !== incompleteMustacheTemplateFill) {
        failureDataJson = JSON.parse(decodeURIComponent(failureData))
      }

      const enhancedFailureData = {
        applicationName: notification.messageVariables.applicationNameKey,
        ...failureDataJson
      }

      return {
        ...notification,
        mustacheTemplate:
          templatePartsFirstPass[0] +
          splitKey1 +
          encodeURIComponent(JSON.stringify(enhancedFailureData)) +
          splitKey2 +
          templatePartsSecondPass[1]
      }
    } catch (e) {
      return notification
    }
  }, [])

  const rowRenderer = ({ key, index, style, parent, isScrolling }) => {
    isListScrolling.current = isScrolling
    return (
      <CellMeasurer
        key={key}
        rowIndex={index}
        parent={parent}
        cache={cellMeasurerCache}
      >
        {({ registerChild }) => {
          const sharedRowProps: {
            index: number
            style: CSSProperties
            getRef: NotificationRowLayoutGetRef
          } = {
            index,
            style,
            getRef: registerChild
          }

          // Prepend custom notification rows
          if (index < fixedTopRowCount) {
            if (
              fixedTopNotificationRows[index] ===
              'TOP_NOTIFICATION_FREEMIUM_USER'
            ) {
              return (
                <FreemiumNewUserNotification
                  {...sharedRowProps}
                  organizationUuid={organizationUuid}
                />
              )
            }

            if (
              fixedTopNotificationRows[index] ===
              'TOP_NOTIFICATION_VULNS_PENDING'
            ) {
              return (
                <VulnsPendingStatusNotification
                  {...sharedRowProps}
                  organizationUuid={organizationUuid}
                  {...vulnsPendingApprovalResponse}
                />
              )
            }
          }

          const notificationIndex = fixedTopRowCount
            ? index - fixedTopRowCount
            : index
          const notification = notifications[notificationIndex]

          // Show notification row loading skeleton
          if (!notification) {
            return (
              <div style={style}>
                <NotificationSkeleton />
              </div>
            )
          }

          const preprocessedNotification = preprocessNotification(notification)

          // Show standard notification row
          return (
            <NotificationRow
              {...sharedRowProps}
              key={`notification-row-${key}`}
              notification={{
                ...preprocessedNotification,
                read:
                  markAllAsReadTimestamp &&
                  markAllAsReadTimestamp > notification.timestamp
                    ? true
                    : notification.read
              }}
              isMarkAsReadLoading={isMarkAsReadLoading}
              markNotificationRead={markNotificationRead}
              mustacheTemplateClickHandler={getClickHandler(notification.type)}
            />
          )
        }}
      </CellMeasurer>
    )
  }

  useLayoutEffect(() => {
    // if the previousRemoteCount is 0, then this is initial load, so don't run the effect in that case
    // never run this effect if the use is scrolling
    if (
      previousRemoteCount.current < remoteCount &&
      previousRemoteCount.current !== 0 &&
      !!listRef.current &&
      !isListScrolling.current
    ) {
      const totalNewNotifications = remoteCount - previousRemoteCount.current
      const newStartIndex =
        previousScrollStartIndex.current + totalNewNotifications
      // set the refs to the new start index
      previousScrollStartIndex.current = newStartIndex
      currentScrollStartIndex.current = newStartIndex
      // new notifications have come in, forceUpdateGrid to recompute heights
      listRef.current.forceUpdateGrid()
      const topOfRowToScrollTo = listRef.current.getOffsetForRow({
        alignment: 'start',
        index: newStartIndex
      })
      listRef.current.scrollToPosition(topOfRowToScrollTo)
    }
    // always update the previousRemoteCount ref
    previousRemoteCount.current = remoteCount
  }, [remoteCount])

  const handleRouteChange = () => {
    popoverProps.onClose(false)
  }

  useEffect(() => {
    window.addEventListener('hashchange', handleRouteChange)

    return () => {
      window.removeEventListener('hashchange', handleRouteChange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const closeNotificationDialog = () => {
    setIsNotificationDialogOpen(false)
  }

  return (
    <>
      <Modal
        isOpen={isNotificationDialogOpen}
        size="md"
        variant="info"
        title={notificationDialogTitle}
        onClose={() => {
          setNotificationDialogContent(<></>)
          setNotificationDialogTitle('')
          closeNotificationDialog()
        }}
        Icon={IconInformation}
      >
        <ModalBody>
          <Text font="body">{notificationDialogContent}</Text>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={closeNotificationDialog}>
            {intl.formatMessage({ id: 'detail_notificationsDialog_okButton' })}
          </Button>
        </ModalFooter>
      </Modal>

      <NotificationsTrigger
        getRef={popoverTriggerProps.ref}
        onClick={() => toggleMenu()}
        organizationUuid={organizationUuid}
        isMenuOpen={!!popoverProps.isOpen}
      />
      <Popover
        {...popoverProps}
        style={{
          zIndex: 1002
        }}
        height={460}
        testId="notifications-popover"
      >
        <NotificationLayout>
          <NotificationHeader
            isMarkAllAsReadLoading={isMarkAllAsReadLoading}
            markAllAsRead={markAllNotificationsRead}
            routeToAccountNotifications={routeToAccountNotifications}
          />
          {notifications.length === 0 && !isLoading ? (
            <>
              <Text
                font="body"
                className={css`
                  text-align: center;
                `}
              >
                {intl.formatMessage({
                  id: 'detail_notifications_nothingToSee'
                })}
              </Text>
              <NotificationFooter
                routeToAccountNotifications={routeToAccountNotifications}
              />
            </>
          ) : (
            <div
              data-testid="notifications-menu-content"
              className={css`
                height: ${dimensions.height}px;
              `}
            >
              {(notifications.length === 0 && isLoading && (
                <>
                  <NotificationSkeleton />
                  <NotificationSkeleton />
                  <NotificationSkeleton />
                  <NotificationSkeleton />
                </>
              )) || (
                <InfiniteLoader
                  remoteCount={remoteCount}
                  isRowLoaded={isRowLoaded}
                  loadMoreRows={requestNextPage}
                  rowCount={rowCount}
                >
                  {({ onRowsRendered, registerChild }) => (
                    <List
                      height={dimensions.height}
                      width={dimensions.width}
                      onRowsRendered={(args) => {
                        // this function runs BEFORE the layout effect so if new notifications come in, the current start index includes them
                        // we want the previous start index without the new notifications
                        previousScrollStartIndex.current =
                          currentScrollStartIndex.current
                        currentScrollStartIndex.current = args.startIndex
                        onRowsRendered(args)
                      }}
                      ref={(ref) => {
                        registerChild(ref)
                        listRef.current = ref
                      }}
                      rowHeight={cellMeasurerCache.rowHeight}
                      rowRenderer={rowRenderer}
                      rowCount={rowCount + fixedTopRowCount}
                      deferredMeasurementCache={cellMeasurerCache}
                      columnWidth={cellMeasurerCache.columnWidth}
                    />
                  )}
                </InfiniteLoader>
              )}
            </div>
          )}
        </NotificationLayout>
      </Popover>
    </>
  )
}
